/**
 * Talking to Scout's API at all: one request function and the error it raises.
 *
 * Not identity-specific, and shared from here rather than from a package of its
 * own because identity and the brief feature are its only callers. It moves out
 * the moment a third one appears that does not want the rest of this package.
 */

/** Pulls the API's error message out of a failed response body. */
function errorMessage(body: unknown, status: number): string {
  if (
    typeof body === 'object' &&
    body !== null &&
    'error' in body &&
    typeof body.error === 'string'
  ) {
    return body.error
  }
  return `request failed with ${String(status)}`
}

/** Raised when a request is refused for a reason the caller should act on. */
export class ApiError extends Error {
  readonly status: number

  constructor(message: string, status: number) {
    super(message)
    this.name = 'ApiError'
    this.status = status
  }
}

export async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`/api${path}`, {
    headers: { 'Content-Type': 'application/json' },
    // The anonymous brief is held by a cookie rather than by an id in the URL,
    // so a request that does not carry cookies reaches nothing.
    credentials: 'same-origin',
    ...init,
  })

  const body: unknown = await response.json().catch((): unknown => null)

  if (!response.ok) {
    throw new ApiError(errorMessage(body, response.status), response.status)
  }

  // The one place the API's shape is taken on trust. Responses are not
  // validated at runtime: this frontend and the Go handlers ship together, so
  // a mismatch is a build failure rather than hostile input. If Scout ever
  // serves a third party, a parser goes here.
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
  return body as T
}
